/* eslint-disable no-undef */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes debounce

interface VersionCheckResult {
  newVersionAvailable: boolean;
  currentVersion: string;
  latestVersion: string | null;
  dismiss: () => void;
  refresh: () => void;
}

export function useVersionCheck(): VersionCheckResult {
  const currentVersion = __BUILD_SHA__;
  const { pathname } = useLocation();

  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastCheckRef = useRef(0);
  const dismissedVersionRef = useRef<string | null>(null);

  const checkVersion = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckRef.current < CHECK_INTERVAL_MS) {
      return;
    }
    lastCheckRef.current = now;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/version.json', {
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data?.version) {
        setLatestVersion(data.version);

        // Reset dismissed state if a newer version appears
        if (data.version !== dismissedVersionRef.current) {
          setDismissed(false);
        }
      }
    } catch (error) {
      // Fail silently — version.json may not exist in local dev
      logger.debug('Failed to fetch version.json', error);
    }
  }, []);

  // Check on route changes — scheduled via setTimeout to satisfy lint rules
  useEffect(() => {
    const id = setTimeout(() => { checkVersion(); }, 0);
    return () => clearTimeout(id);
  }, [pathname, checkVersion]);

  // Check on tab visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    dismissedVersionRef.current = latestVersion;
  }, [latestVersion]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  const newVersionAvailable =
    !dismissed &&
    latestVersion !== null &&
    latestVersion !== currentVersion;

  return {
    newVersionAvailable,
    currentVersion,
    latestVersion,
    dismiss,
    refresh,
  };
}
